import { useMemo, useState } from 'react';

/**
 * Paginación y ordenamiento de una grilla de React/MUI.
 *
 * Reemplaza dos cosas que están a mano en 26 grillas:
 *
 *  - el handler de ordenamiento, que **borra una clave por columna para setear una sola**
 *    (37 líneas en tickets, 28 en móviles). Acá el estado es `{ campo, direccion }`: una
 *    columna ordenada, no 32 claves de las que 31 están vacías;
 *  - `pagination` con sus dos handlers, y el detalle de volver a la primera página al
 *    reordenar — que tickets hacía con `pagination.page = 0`, **mutando el estado**: el
 *    query pedía la página 0 pero el paginador seguía mostrando la que estaba.
 *
 * EL CONTRATO CON EL BACKEND NO CAMBIA. `sortQuery` sigue exponiendo el objeto con una clave
 * por columna, que es lo que consumen el armado del query y los encabezados. El refactor es
 * de la forma interna del estado, no de lo que viaja: por eso la lista de `campos`.
 */

/**
 * Siguiente estado al clickear una columna, con el ciclo que ya tenían las grillas: sin orden
 * pasa a ascendente, ascendente a descendente, y descendente vuelve a ascendente (no hay
 * forma de volver a "sin orden", y se mantiene así para no cambiar el comportamiento).
 */
export const siguienteOrden = (sort, campo) => {
	const actual = sort && sort.campo === campo ? sort.direccion : '';
	return { campo: campo, direccion: actual === 'asc' ? 'desc' : 'asc' };
};

/**
 * El estado `{campo, direccion}` llevado al objeto con una clave por columna que espera el
 * armado del query. Las columnas que no están ordenadas viajan vacías, no ausentes: el
 * backend recibe todas las claves (ver `buildQueryString`).
 */
export const aSortQuery = (campos, sort) => {
	const query = {};
	(campos || []).forEach((campo) => {
		query[campo] = sort && sort.campo === campo ? sort.direccion : '';
	});
	return query;
};

/**
 * @param campos      nombres de las columnas ordenables, los que espera el backend
 * @param sortInicial `{ campo, direccion }` con el que abre la grilla
 * @param perPage     tamaño de página inicial (default 10)
 */
const useGridState = (opciones) => {
	const config = opciones || {};
	const campos = config.campos || [];
	const sortInicial = config.sortInicial || { campo: '', direccion: '' };

	const [pagination, setPagination] = useState({ page: 0, perPage: config.perPage || 10 });
	const [sort, setSort] = useState(sortInicial);

	// Reordenar vuelve a la primera página: la que estaba no significa lo mismo con otro orden.
	const requestSort = (campo) => {
		setSort((prev) => siguienteOrden(prev, campo));
		setPagination((prev) => Object.assign({}, prev, { page: 0 }));
	};

	// Memoizado: las grillas usan el query como dependencia del efecto que trae los datos, así
	// que un objeto nuevo en cada render sería una consulta al backend por render.
	const sortQuery = useMemo(() => aSortQuery(campos, sort), [campos, sort]);

	return {
		pagination: pagination,
		sort: sort,
		/** El objeto con una clave por columna, para el query. */
		sortQuery: sortQuery,
		requestSort: requestSort,
		setPage: (page) => setPagination((prev) => Object.assign({}, prev, { page: page })),
		setPerPage: (perPage) => setPagination({ page: 0, perPage: perPage }),
		reset: () => {
			setPagination({ page: 0, perPage: config.perPage || 10 });
			setSort(sortInicial);
		},
	};
};

export default useGridState;
